<!-- eslint-disable max-lines -->
<template>
  <RequireAdContract>
    <div class="content">
      <div class="row align-center">
        <div class="large-11 columns">
          <h1 class="content__title heading">
            <div class="heading__start">
              <span>{{ $t('ADVERTISEMENT.BUDGET.TITLE') }}</span>
            </div>
          </h1>
          <br />

          <div class="budget-btns-container">
            <ElButton
              v-if="$auth.check('advertisement')"
              native-type="button"
              type="primary"
              icon="el-icon-plus"
              @click.native="openBudgetModal"
            >
              {{ $t('ADVERTISEMENT.BUDGET.CHARGE_CREDIT') }}
            </ElButton>
          </div>
          <div class="row">
            <div :class="['small-12', 'columns', 'large-12']">
              <div class="card balance card--bordered card--success">
                <div class="card__block">
                  <div
                    v-dompurify-html="
                      $t('ADVERTISEMENT.BUDGET.DEVELOPER_CREDIT')
                    "
                    class="card__header"
                  />
                  <div>
                    <span v-if="!isRequestingDeveloperCredit && hasNoCredit">
                      <span class="small">
                        <i class="material-icons text-warning">
                          error
                        </i>
                        {{ $t('ADVERTISEMENT.BUDGET.NO_CREDIT') }}
                      </span>
                    </span>
                    <span
                      v-else-if="!isRequestingDeveloperCredit"
                      class="balance__amount balance__info"
                    >
                      <span>{{ currencyFormat(developerCredit) }}</span>
                      <small
                        :class="[
                          'balance__currency',
                          { invisible: hasNoCredit },
                        ]"
                      >
                        {{ $t('RIALS') }}
                      </small>
                    </span>
                    <HollowDotsSpinner
                      v-else-if="isRequestingDeveloperCredit"
                      class="balance__loading"
                      :animation-duration="1000"
                      :dot-size="15"
                      :dots-num="3"
                      :color="'#95a5a6'"
                    />
                  </div>
                </div>
                <img
                  class="balance__icon"
                  :src="require('@/assets/images/moneybag.svg')"
                />
              </div>
            </div>
          </div>
          <div class="small-12 large-12 columns">
            <InternalNavbar :navigation="navigation" />
          </div>

          <hr class="divider hide-for-large" />
          <RouterView />
        </div>
      </div>
      <CreditChargeModal v-model="showChargeModal" />
    </div>
  </RequireAdContract>
</template>

<script>
import RequireAdContract from '../../common/RequireAdContract';
import HollowDotsSpinner from '@@/common/HollowDotsSpinner';
import InternalNavbar from '@@/layout/InternalNavbar';

import { AD_FINANCE_BUDGET_BUTTON } from '@/constants/gtm-events';

import CreditChargeModal from './CreditChargeModal';
import { Advertisement } from '@/api/api';
import { format as currencyFormat } from '@/utils/number';
import modal from '@/mixins/modal';
import form from '@/mixins/form';
import layout from '@/mixins/layout';
import staging from '@/mixins/staging';
import { dateFormat } from '@/utils/date';
import lang from '@/mixins/lang';
import createController from '@/utils/controller';

export default {
  components: {
    InternalNavbar,
    HollowDotsSpinner,
    CreditChargeModal,
    RequireAdContract,
  },
  mixins: [modal, form, layout, staging, lang],

  data() {
    return {
      credit: 0,
      apiError: false,
      creditAPIError: false,
      chargeAPIError: false,
      isRequestingDeveloperCredit: false,
      showChargeModal: false,
    };
  },

  computed: {
    developerCredit() {
      return this.credit;
    },

    navigation() {
      const navs = [
        {
          to: { name: 'finance-transactions' },
          text: this.$t('ADVERTISEMENT.FINANCE.TRANSACTIONS'),
        },
      ];
      if (this.isStaging) {
        navs.push({
          to: { name: 'finance-invoice' },
          text: this.$t('ADVERTISEMENT.FINANCE.INVOICE'),
        });
      }
      return navs;
    },

    hasNoCredit() {
      return this.credit === 0;
    },
  },
  created() {
    this.setPageTitle('ADVERTISEMENT.TITLE');

    this.getDeveloperCredit();
  },

  methods: {
    currencyFormat,
    openBudgetModal() {
      this.$gtm.trackEvent(AD_FINANCE_BUDGET_BUTTON);

      this.openModal('showChargeModal');
    },

    translateDate(date) {
      let toFormat = 'DD/MM/YYYY HH:mm';

      if (this.lang === 'fa') {
        toFormat = 'jYYYY/jMM/jDD HH:mm';
      }

      return dateFormat()(toFormat)(date);
    },

    getDeveloperCredit() {
      this.isRequestingDeveloperCredit = true;
      return Advertisement.getDeveloperCredit()
        .then(({ body: { credit = 0 } }) => {
          this.credit = credit;
          this.isRequestingDeveloperCredit = false;
          this.creditAPIError = false;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
          this.creditAPIError = true;
        });
    },
  },
};
</script>
<style lang="scss">
.balance {
  padding: 0;
  position: relative;
  overflow: hidden;

  &__amount {
    font-size: $global-font-size * 2;
    font-weight: 700;
    margin-top: $global-margin * 0.4;
  }

  &__info {
    small {
      color: $lb-color-secondary;
      font-size: $small-font-size;
    }
  }

  &__currency {
    color: $lb-color-secondary;
    font-size: $global-font-size * 0.5;
    font-weight: normal;
    vertical-align: 2px;
  }

  &__icon {
    position: absolute;
    width: 135px;
    opacity: 0.1;
    right: -42px;
    bottom: -17px;
    z-index: 10001; // because of weird el-loading z-index ~ 100000

    @include rtl() {
      right: auto;
      left: -42px;
    }
  }

  &__loading {
    min-height: 55px !important;
    display: flex;
    align-items: center;
  }
}

.budget-btns-container {
  display: flex;
}
</style>
